import { useNotification } from '@eye-able/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { T } from '@/i18n/T';
import { SIGNED_URL_EXPIRED_EVENT } from '@/http/fetchWrapper';

export default function ReactQueryServerErrorNotifications() {
  const { t } = useInlineTranslation();
  const notification = useNotification();
  const queryClient = useQueryClient();

  // A signed CDN download URL (report-storage.eye-able.com) expired — fired
  // by fetchWrapper for any call site, not just React Query ones (plain
  // button-click downloads included), so it's a plain window event rather
  // than a query-cache subscription like the one below.
  useEffect(() => {
    const handleSignedUrlExpired = () => {
      notification.error(
        <span>
          <T k='signedUrlExpired' />{' '}
          <a
            href='#'
            onClick={() => window.location.reload()}
            className='eye:cursor-pointer eye:border-0 eye:bg-transparent eye:p-0 eye:font-semibold eye:underline'
          >
            {t('refreshPage')}
          </a>
        </span>,
        {
          id: 'signed-url-expired',
          duration: Infinity,
        },
      );
    };

    window.addEventListener(SIGNED_URL_EXPIRED_EVENT, handleSignedUrlExpired);
    return () =>
      window.removeEventListener(
        SIGNED_URL_EXPIRED_EVENT,
        handleSignedUrlExpired,
      );
  }, [notification]);

  useEffect(() => {
    const shouldNotify = (error: unknown) => {
      const typedError = error as {
        status?: number | string;
        response?: { status?: number | string };
        name?: string;
        message?: string;
      };

      const rawStatus = typedError?.status ?? typedError?.response?.status;
      const status =
        typeof rawStatus === 'string' ? Number(rawStatus) : rawStatus;

      const isServerError =
        typeof status === 'number' && status >= 500 && status < 600;
      const isNetworkError =
        rawStatus == null &&
        (error instanceof TypeError ||
          typedError?.name === 'TypeError' ||
          /failed to fetch|network|load failed/i.test(
            typedError?.message || '',
          ));

      return isServerError || isNetworkError;
    };

    const unsubscribeQueries = queryClient
      .getQueryCache()
      .subscribe((event) => {
        if (event?.type !== 'updated') return;

        const query = event.query;

        if (query.state.status !== 'error') return;
        // Opted-in pages show initial failures at their route boundary.
        if (query.meta?.handlesInitialError && query.state.data === undefined)
          return;
        if (!shouldNotify(query.state.error)) return;

        notification.error(t('errorOccurred'));
      });

    const unsubscribeMutations = queryClient
      .getMutationCache()
      .subscribe((event) => {
        if (event?.type !== 'updated') return;

        const mutation = event.mutation;

        if (mutation.state.status !== 'error') return;
        if (!shouldNotify(mutation.state.error)) return;

        notification.error(t('errorOccurred'));
      });

    return () => {
      unsubscribeQueries();
      unsubscribeMutations();
    };
  }, [notification, queryClient, t]);

  return null;
}
